import type { BalanzaEquationSide } from '../../core/types/payloads/explora.types';

export function isInverseOperationsPayload(payload: {
  balanceMode?: string;
  equationLeft?: BalanzaEquationSide;
  equationRight?: BalanzaEquationSide;
  skin?: string;
}): boolean {
  if (payload.balanceMode === 'inverse-operations') return true;
  return Boolean(payload.equationLeft && payload.equationRight && payload.skin === 'algebra');
}

function sideToAlgebra(side: BalanzaEquationSide): string {
  const parts: string[] = [];
  if (side.xBoxes > 0) {
    parts.push(side.xBoxes === 1 ? 'x' : `${side.xBoxes}x`);
  }
  if (side.constants !== 0) {
    const abs = Math.abs(side.constants);
    if (parts.length === 0) {
      parts.push(String(side.constants));
    } else if (side.constants > 0) {
      parts.push(`+ ${abs}`);
    } else {
      parts.push(`− ${abs}`);
    }
  }
  return parts.length > 0 ? parts.join(' ') : '0';
}

export function formatBalanzaEquation(left: BalanzaEquationSide, right: BalanzaEquationSide): string {
  const leftStr = sideToAlgebra(left);
  const rightStr = sideToAlgebra(right);

  if (left.xBoxes === 1 && left.constants === 0 && right.xBoxes === 0) {
    return `x = ${right.constants}`;
  }
  if (left.xBoxes > 0 && left.constants === 0 && right.xBoxes === 0) {
    return `${leftStr} = ${right.constants}`;
  }
  return `${leftStr} = ${rightStr}`;
}

export function isEquationIsolated(left: BalanzaEquationSide, right: BalanzaEquationSide): boolean {
  return left.xBoxes === 1 && left.constants === 0 && right.xBoxes === 0 && right.constants >= 0;
}

export function applyOperationToSide(
  side: BalanzaEquationSide,
  op: 'BOTH_ADD' | 'BOTH_SUBTRACT' | 'BOTH_MULTIPLY' | 'BOTH_DIVIDE',
  operand: number,
): BalanzaEquationSide | null {
  if (!Number.isFinite(operand)) return null;

  const next: BalanzaEquationSide = { ...side };

  switch (op) {
    case 'BOTH_ADD':
      next.constants += operand;
      return next;
    case 'BOTH_SUBTRACT':
      next.constants -= operand;
      return next;
    case 'BOTH_MULTIPLY':
      if (operand === 0) return null;
      next.xBoxes *= operand;
      next.constants *= operand;
      return next;
    case 'BOTH_DIVIDE':
      if (operand === 0) return null;
      if (next.xBoxes % operand !== 0 || next.constants % operand !== 0) return null;
      next.xBoxes /= operand;
      next.constants /= operand;
      return next;
    default:
      return null;
  }
}

export function defaultInverseDemo(): {
  equationLeft: BalanzaEquationSide;
  equationRight: BalanzaEquationSide;
  displayEquation: string;
} {
  const equationLeft = { xBoxes: 2, constants: 4 };
  const equationRight = { xBoxes: 0, constants: 10 };
  return {
    equationLeft,
    equationRight,
    displayEquation: formatBalanzaEquation(equationLeft, equationRight),
  };
}

export function defaultInverseOperations() {
  return [
    { id: 'sub4', label: '➖ Restar 4 a ambos lados', internalValue: 4, type: 'BOTH_SUBTRACT' as const },
    { id: 'div2', label: '➗ Dividir ambos lados por 2', internalValue: 2, type: 'BOTH_DIVIDE' as const },
    { id: 'add2', label: '➕ Sumar 2 a ambos lados', internalValue: 2, type: 'BOTH_ADD' as const },
  ];
}
